import { RiAddLine } from 'component-library';
import { useMemo } from 'react';

type AutocompleteItem = {
  key: string;
  label: string;
  meta?: string;
};

type AutocompleteCreateFieldProps<T> = {
  items: T[];
  getItem: (item: T) => AutocompleteItem;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  onSelectItem: (item: T) => void;
  onCreateNew: () => void;
  placeholder: string;
  noResultsLabel: string;
  createOptionLabel: (name: string) => string;
  disabled?: boolean;
  inputClassName?: string;
};

/**
 * Hybrid input/selector for picking an existing item by name (with autocomplete
 * filtering) or creating a new one from the typed value. Generic over the item
 * shape so it can be reused anywhere a "search or create" pattern is needed
 * (draft plot groups, plot labels, etc.).
 */
export const AutocompleteCreateField = <T,>({
  items,
  getItem,
  searchValue,
  onSearchValueChange,
  onSelectItem,
  onCreateNew,
  placeholder,
  noResultsLabel,
  createOptionLabel,
  disabled,
  inputClassName,
}: AutocompleteCreateFieldProps<T>) => {
  const trimmedSearch = searchValue.trim();
  const filteredItems = useMemo(
    () =>
      trimmedSearch
        ? items.filter((item) => {
            const { label, meta } = getItem(item);
            const search = trimmedSearch.toLowerCase();
            return label.toLowerCase().includes(search) || meta?.toLowerCase().includes(search);
          })
        : items,
    [items, trimmedSearch, getItem],
  );
  const hasExactMatch = items.some((item) => getItem(item).label.toLowerCase() === trimmedSearch.toLowerCase());

  return (
    <div className='flex flex-col gap-2'>
      <input
        value={searchValue}
        onChange={(e) => onSearchValueChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && trimmedSearch && !hasExactMatch) onCreateNew();
        }}
        className={
          inputClassName ?? 'typography-body1 w-full bg-transparent outline-none placeholder:text-text-disabled'
        }
      />

      <div className='max-h-56 overflow-y-auto rounded-lg border border-neutral-black-12'>
        {filteredItems.length === 0 && !trimmedSearch && (
          <div className='typography-body2 px-3 py-2 text-text-secondary'>{noResultsLabel}</div>
        )}
        {filteredItems.map((item) => {
          const { key, label, meta } = getItem(item);
          const showMeta = Boolean(meta) && meta !== label;
          return (
            <button
              key={key}
              type='button'
              disabled={disabled}
              onClick={() => onSelectItem(item)}
              className='flex w-full px-3 py-2 text-left hover:bg-neutral-black-4 disabled:cursor-not-allowed'
            >
              {showMeta ? (
                <span className='flex flex-col leading-tight'>
                  <span className='typography-caption text-text-secondary'>{meta}</span>
                  <span className='typography-body2 text-text-primary'>{label}</span>
                </span>
              ) : (
                <span className='typography-body2 text-text-primary'>{label}</span>
              )}
            </button>
          );
        })}
        {trimmedSearch && !hasExactMatch && (
          <button
            type='button'
            disabled={disabled}
            onClick={onCreateNew}
            className='typography-body2Semibold flex w-full items-center gap-2 border-t border-neutral-black-12 px-3 py-2 text-left text-primary-100 hover:bg-neutral-black-4 disabled:cursor-not-allowed'
          >
            <RiAddLine size={14} className='shrink-0' />
            {createOptionLabel(trimmedSearch)}
          </button>
        )}
      </div>
    </div>
  );
};
